/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState, useEffect } from 'react';
import { withStyles } from '@material-ui/core/styles';
import {
    object, shape, arrayOf, string, bool,
} from 'prop-types';
import { useHistory } from 'react-router';
import { Add, Remove } from '@material-ui/icons';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { useUIDSeed } from 'react-uid';
import { useDispatch, useSelector } from 'react-redux';
import Expansion from '@material-ui/core/ExpansionPanel';
import ExpansionSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpansionDetails from '@material-ui/core/ExpansionPanelDetails';
import ReactMarkdown from 'react-markdown';
import DesktopMobile from '../../../helpers/DesktopMobile/DesktopMobile';
import customBreakpoints from '../../../helpers/customBreakpoints';
import { trackData } from '../../../helpers/tracking/common/commonTrackingHelpers';
import GraphqlSeoHeadTags from '../GraphqlCommonComponents/GraphqlSeoHeadTags/GraphqlSeoHeadTags';
import GraphqlDisclaimer from '../GraphqlCategoryPage/Partials/GraphqlDisclaimer/GraphqlDisclaimer';
import GraphqlSideRailBanner from '../GraphqlCategoryPage/Partials/GraphqlLeftMenuContainer/Partials/GraphqlSideRailBanner';
import { getSSRDeviceType } from '../../../../state/ducks/App/App-Selectors';
import { getPresentationFamily } from '../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { trackEvent } from '../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';

const styles = (theme)  => ({
    container: {
        [theme?.breakpoints?.down('sm')]: {
            maxWidth: theme?.palette?.tiers?.container?.maxWidth?.downSmall,
            margin: '0 auto',
        },
        [theme?.breakpoints?.down('md')]: {
            maxWidth: theme?.palette?.tiers?.container?.maxWidth?.downMedium,
            margin: '0 auto',
        },
        [theme?.breakpoints?.up('md')]: {
            maxWidth: 1440,
            margin: '0 auto',
        },
        [theme?.breakpoints?.up('lg')]: {
            maxWidth: 1440,
            margin: '0 auto',
        },
        padding: 16,
        '& a': {
            outline: 'none',
        },
    },
    headerBlock: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: 25,
    },
    headerTitle: {
        display: 'flex',
        margin: 0,
        flexBasis: '27%',
        maxWidth: '27%',
        '& span': {
            fontSize: 20,
            lineHeight: 2,
            borderRight: `2px solid ${theme?.palette?.ShrBordercolor}`,
            marginRight: 36,
            paddingRight: 36,
            textTransform: 'uppercase',
        },
    },
    mobileHeaderBlock: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0 8px 8px',
        boxShadow: '0 8px 4px -4px rgba(59,33,81,0.21)',
        marginBottom: '10px',
    },
    mobileHeaderTitle: {
        fontSize: 17,
    },
    showCopyButton: {
        color: theme.palette.cms?.primary || theme.palette.colorPrimary,
        outline: 0,
    },
    headerSubTitle: {
        flex: '1 1 auto',
        lineHeight: '1.3',
    },
    ecardBlock: {
        display: 'block',
        alignItems: 'center',
    },
    ecard: {
        width: '75%',
        margin: '16px auto',
        justifyContent: 'center',
        display: 'flex',
        flexWrap: 'wrap',
        boxSizing: 'border-box',
        '& a': {
            textDecoration: 'none',
        },
        '& p': {
            textAlign: 'center',
            fontSize: 14,
            margin: '5px 0',
        },
        [theme.breakpoints.down(customBreakpoints.tabPortrait)]: {
            width: '100%',
            '& > div': {
                maxWidth: '50%',
                flexBasis: '50%',
            },
        },
        '& > div': {
            textAlign: 'center',
        },
    },
    ecardImage: {
        padding: 10,
        transition: 'all 0.4s ease-in-out 0s',
        display: 'block',
        '& img': {
            width: '85%',
            margin: '0 auto',
        },
        '&:hover': {
            padding: 0,
        },
    },
    ecardName: {
        color: theme?.palette?.common?.black,
        fontWeight: 700,

    },
    ecardTitle: {
        fontWeight: 400,
        marginBottom: 0,
        color: theme?.palette?.common?.black,
        fontSize: 24,
        textAlign: 'center',
        [theme.breakpoints.down('xs')]: {
            fontSize: 22,
        },
    },
    ecardOcasionLink: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
    },
    occasionTitle: {
        color: theme.palette.cms?.primary || theme.palette.colorPrimary,
        margin: '0 10px',
        cursor: 'pointer',
        '&:not(:last-child):after': {
            content: '"|"',
            marginLeft: 20,
        },
    },
    ecardFree: {
        color: theme?.palette?.grey5,
    },
    ecardDelivery: {
        color: theme.palette.cms?.primary || theme.palette.colorPrimary,
    },
    ecardContent: {
        margin: '5px 0 15px',
    },
    cardHeadingStyle: {
        '& h1, h2, h3, h4, h5, h6': {
            margin: 0,
            color: theme.palette.cms?.black || '#000',
        },
        '& p': {
            color: theme.palette.cms?.black || '#000',
            fontWeight: 700,
        },
    },
    ecardHeadContainer: {
        boxShadow: 'none',
    },
    ecardHeading: {
        marginTop: 10,
        '& div': {
            margin: 0,
            flexGrow: 'inherit',
        },
        '& h2': {
            margin: 0,
        },
        '& span.MuiIconButton-label': {
            background: theme.palette?.colorPrimary || '#201648',
            borderRadius: '50%',
            '& svg': {
                color: theme.palette?.common?.white || '#fff',
                width: 22,
                height: 22,
            },
        },
    },
    ecardLeftContainer: {
        width: '100%',
        justifyContent: 'center',
        '& a': {
            margin: '15px 0',
            display: 'block',
            '&::after': {
                display: 'none',
                content: '',
            },
        },
        '& h3': {
            margin: '10px 0px',
        },
    },
    ecardsContainerSize: {
        maxWidth: '80%',
        flexBasis: '80%',
        margin: '0 auto',
    },
    ecardSize: {
        width: '100%',
    },
    leftSideContainer: {
        maxWidth: '20%',
        flexDirection: 'column',
    },
    link: {
        color: theme.palette?.colorPrimary || '#201648',
    },
});

const splitString = (copy, classes) => {
    const ecardDeliveryName = copy?.split('\n');
    const [free, delivery] = ecardDeliveryName;
    return (
        <>
            <Typography className={classes.ecardFree}>{free}</Typography>
            <Typography className={classes.ecardDelivery}>{delivery}</Typography>
        </>
    );
};

const CardItem = ({
    card, classes, url, isGlobal, isDelayed,
}) => {
    const dispatch = useDispatch();
    const {
        link,
        image,
        copy,
    } = card;
    const cdnLink = link?.href?.includes('www') || link?.href?.includes('cdn')  || link?.href?.indexOf('http://') === 0 || link?.href?.indexOf('https://') === 0;
    const title = link?.title || '';
    const qualifiedUrl = (isGlobal || isDelayed) === true && !cdnLink ? url + link?.href : link?.href; // if global, concatenate the domain from the global map, else use the href value from the custom entry.
    const img = image?.url ? `${image?.url}?quality=100&auto=webp` : 'https://images.contentstack.io/v3/assets/blt95e0138311135214/blt10b69a6fccf09b45/60483d2ffa12da5a616589c6/image404.jpeg';

    let urlExtract = {};
    try {
        urlExtract = new URL(qualifiedUrl);
    } catch (e) {
        urlExtract = {};
    }
    const id = image?.filename.replace?.('.', '-');
    return (
        <Grid
            item
            xs={3}
        >
            <a
                id={id}
                className={classes.link}
                href={qualifiedUrl}
                target="__blank"
                onClick={() => {
                    dispatch(trackEvent({
                        eventCategory: 'file_download',
                        eventAction: 'file_download',
                        eventLabel: 'file_download',
                        ga_event_name: 'file_download',
                        file_name: image?.filename || '',
                        file_extension: image?.content_type || '',
                        link_classes: classes?.link || '',
                        link_domain: urlExtract?.hostname || '',
                        link_protocol: urlExtract?.protocol || '',
                        link_id: id,
                        link_text: 'Digital Download',
                        link_url: img,
                    }));
                }}
            >
                <div className={classes.ecardImage}>
                    <img
                        src={img}
                        alt={title}
                    />
                </div>
                <div className={classes.ecardContent}>
                    <ReactMarkdown className={classes.cardHeadingStyle} source={title} escapeHtml={false} />
                    {splitString(copy, classes)}
                </div>
            </a>
        </Grid>
    );
};

CardItem.propTypes = {
    card: shape({
        link: shape({
            title: string.isRequired,
            href: string.isRequired,
        }),
        image: {
            url: string.isRequired,
        },
        copy: string.isRequired,
    }).isRequired,
    classes: object.isRequired,
    url: string.isRequired,
    isGlobal: bool.isRequired,
    isDelayed: bool.isRequired,
};

// example  before Get Well after showing  get-well
const covertString = (name) => name.replace(/\s+/g, '-').toLowerCase(); // regex to create the id and this id is used to target the scrolling block id

const EcardsBlock = ({
    data, classes, domain, isGlobal, isDelayed,
}) => {
    const {
        imoc: {
            header,
            copy,
        },
        ecard_occasion_section,
        ocassion_links,
        seo = {},
        lander_url_mapping,
        dropdown,
        side_rail_banner = {},
    } = data;

    const history = useHistory();
    const cardTargetId = history?.location?.hash || '';
    // on page load it scrolls down to the particular section based on targetId
    useEffect(() => {
        let scrollTimer;
        if (typeof document !== 'undefined' && typeof window !== 'undefined' && cardTargetId !== '') {
            const element = document.getElementById(cardTargetId.replace('#', ''));
            scrollTimer = setTimeout(() => {
                window.scrollTo({
                    behavior: element ? 'smooth' : 'auto',
                    top: element ? element.offsetTop : 0,
                });
            }, 600);
        }
        return () => {
            clearTimeout(scrollTimer);
        };
    }, [cardTargetId]);

    const seed = useUIDSeed();
    const ssrDeviceType = useSelector(getSSRDeviceType);
    const isFlowerBrand = useSelector(getPresentationFamily) === 'flower';
    const showLeftOccasionContainer = isFlowerBrand && ssrDeviceType === 'desktop' && dropdown === 'left';
    const showLeftSideRailBanner = (Object.keys(side_rail_banner).length && isFlowerBrand && ssrDeviceType === 'desktop') || false;

    // Persist data layer
    trackData({
        untData: data,
        action: 'track_ecards_data',
    });

    const [showHeaderCopy, setShowHeaderCopy] = useState(false);
    const [expandedData, setExpand] = useState({
        isExpanded: {},
        blockKey: '',
    });
    const { isExpanded } = expandedData;

    const renderHeaderBlock = () => (
        <DesktopMobile
            desktop={() => (
                <Grid item xs={12} className={classes.headerBlock}>
                    {header && <h1 className={classes.headerTitle}><span>{header}</span></h1>}
                    {copy && <div className={classes.headerSubTitle}><ReactMarkdown source={copy} escapeHtml={false} /></div>}
                </Grid>
            )}
            mobile={() => (
                <Grid item xs={12} className={classes.mobileHeaderBlock}>
                    <h1 className={classes.mobileHeaderTitle}><span>{header}</span></h1>
                    <div
                        className={classes.showCopyButton}
                        onClick={() => setShowHeaderCopy(!showHeaderCopy)}
                        onKeyDown={() => { }}
                        role="button"
                        tabIndex={-1}
                    >
                        {showHeaderCopy ? 'Close' : 'Read More'}
                    </div>
                    {showHeaderCopy && (
                        <div className={classes.headerSubTitle}><ReactMarkdown source={copy} escapeHtml={false} /></div>
                    )}
                </Grid>
            )}
        />
    );
    const scrollToBlock = (urlString) => {
        const targetId = urlString.split('#').pop();
        if (typeof document !== 'undefined') {
            document.getElementById(targetId).scrollIntoView({ behavior: 'smooth' });
        }
    };

    // if this is a global ecards collection, use the domain url map in the global entry.
    const getMappedUrl = (brandMap) => {
        const brandObj = brandMap.find((obj) => (obj.key === domain));
        return brandObj.value;
    };
    const url = (isGlobal || isDelayed) && getMappedUrl(lander_url_mapping.brand_lander_url.value);

    const listProduct = (ecards, isExpandBlock = false) => ecards?.length > 0 && ecards.map((card, index) => {
        if (isFlowerBrand && isExpandBlock && isGlobal && isDelayed) {
            return index <= 3 && <CardItem card={card} key={seed(card)} classes={classes} url={url} isGlobal={isGlobal} isDelayed={isDelayed} />;
        }
        return <CardItem card={card} key={seed(card)} classes={classes} url={url} isGlobal={isGlobal} isDelayed={isDelayed} />;
    });
    const cardItemContainer = showLeftOccasionContainer ? `${classes.ecard} ${classes.ecardSize}` : classes.ecard;

    return (
        <>
            {
                seo?.seo_searchable
                    ? (
                        <GraphqlSeoHeadTags seoData={seo} />
                    ) : null
            }

            <Grid container className={classes.container}>
                {renderHeaderBlock()}
                {/* Occasions links on top */}
                {!showLeftOccasionContainer && (
                    <Grid item xs={12} className={classes.ecardOcasionLink}>
                        {ocassion_links?.map((list, key) => {
                            const {
                                title,
                                href,
                            } = list;
                            return (
                                <a href={href} aria-label={title} tabIndex={key} className={classes.occasionTitle} key={seed(list)} onClick={() => scrollToBlock(href)}>{title}</a>
                            );
                        })}
                    </Grid>
                )}
                {/* Occasions links or Side Rail Banner on the left */}
                {(showLeftOccasionContainer || showLeftSideRailBanner) && (
                    <Grid className={classes.leftSideContainer}>
                        {showLeftOccasionContainer
                            && (
                                <Grid item xs={12} className={showLeftOccasionContainer && classes.ecardLeftContainer}>
                                    {ocassion_links?.map((list, key) => {
                                        const {
                                            title,
                                            href,
                                        } = list;
                                        return (
                                            <a href={href} aria-label={title} tabIndex={key} className={classes.occasionTitle} key={seed(list)} onClick={() => scrollToBlock(href)}>{title}</a>
                                        );
                                    })}
                                </Grid>
                            )}
                        {showLeftSideRailBanner
                            && (
                                <Grid>
                                    <GraphqlSideRailBanner bannerData={side_rail_banner} />
                                </Grid>
                            )}
                    </Grid>
                )}
                <Grid item xs={12} className={(showLeftOccasionContainer || showLeftSideRailBanner) && classes.ecardsContainerSize}>
                    {
                        ecard_occasion_section?.map((list, key) => {
                            const {
                                occasion_header,
                                ecards,
                            } = list;
                            return (
                                <Grid
                                    key={seed(list)}
                                    container
                                    id={covertString(occasion_header)}
                                    className={(!showLeftOccasionContainer || !showLeftSideRailBanner || isGlobal || isDelayed) && classes.ecardBlock}
                                >
                                    {ecards?.length > 4 && isFlowerBrand && isGlobal && isDelayed ? (
                                        <Expansion className={classes.ecardHeadContainer}>
                                            <ExpansionSummary
                                                expandIcon={!isExpanded[`panel_${key}`] ? <Add /> : <Remove />}
                                                id={`${key}panel-header`}
                                                aria-controls={`${key}panel-content`}
                                                className={classes.ecardHeading}
                                                onClick={() => {
                                                    const expandPanel = isExpanded;
                                                    expandPanel[`panel_${key}`] = !isExpanded[`panel_${key}`];
                                                    setExpand({ isExpanded: expandPanel, blockKey: key });
                                                }}
                                            >
                                                <Grid item xs={12}>
                                                    {occasion_header && <h2 className={classes.ecardTitle}>{occasion_header}</h2>}
                                                </Grid>
                                            </ExpansionSummary>
                                            <ExpansionDetails>
                                                {isExpanded[`panel_${key}`] && <Grid item className={cardItemContainer}>{listProduct(ecards)} </Grid>}
                                            </ExpansionDetails>
                                        </Expansion>
                                    ) : (
                                        occasion_header && <h2 className={classes.ecardTitle}>{occasion_header}</h2>
                                    )}
                                    {!isExpanded[`panel_${key}`] && <Grid item xs={12} className={cardItemContainer}>{listProduct(ecards, true)}</Grid>}
                                </Grid>
                            );
                        })
                    }
                    <Grid>
                        <GraphqlDisclaimer
                            disclaimerBlock={data.disclaimer}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
};

EcardsBlock.propTypes = {
    classes: object.isRequired,
    domain: bool.isRequired,
    isGlobal: string.isRequired,
    isDelayed: string.isRequired,
    data: shape({
        imoc: shape({
            header: string.isRequired,
            copy: string.isRequired,
        }),
        ocassion_links: arrayOf(
            shape({
                title: string.isRequired,
                href: string.isRequired,
            }),
        ).isRequired,
        ecard_occasion_section: arrayOf(
            shape({
                occasion_header: string.isRequired,
                ecards: arrayOf(
                    shape({
                        link: shape({
                            title: string.isRequired,
                            href: string.isRequired,
                        }),
                        image: {
                            url: string.isRequired,
                        },
                        copy: string.isRequired,
                    }),
                ),
            }),
        ),
        seo: shape({
            breadcrumb: arrayOf(
                shape({
                    title: string.isRequired,
                    href: string.isRequired,
                }).isRequired,
            ),
            keywords: string,
            meta_description: string,
            seo_searchable: bool.isRequired,
            page_title: string,
        }).isRequired,
        lander_url_mapping: shape({
            key: string,
            value: string,
        }),
        side_rail_banner: shape({
            image: shape({
                url: string,
                title: string,
            }),
            link: shape({
                href: string,
                title: string,
            }),
        }),
    }).isRequired,
    dropdown: string.isRequired,
};
export default withStyles(styles)(EcardsBlock);
